import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Modal from '@/components/Modal/Modal';
import classNames from 'classnames';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getActiveBundleStoreGroupsForVariant, ActiveBundleStoreGroupsResponse } from '../deactivateProducts.helper';
import styles from './ProductActiveBundlesModal.module.css';

export interface ProductActiveBundlesModalState {
  variantId: string;
  productName: string;
  productDescription: string;
  storeGroupNamesById: Record<string, string>;
}

export interface ProductActiveBundlesModalProps {
  modalState: ProductActiveBundlesModalState | null;
  activeBundleStoreGroups: ActiveBundleStoreGroupsResponse[];
  isLoading?: boolean;
  isError?: boolean;
  onClose: () => void;
}

const ProductActiveBundlesModal: FC<ProductActiveBundlesModalProps> = ({
  modalState,
  activeBundleStoreGroups,
  isLoading = false,
  isError = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const isOpen = modalState !== null;

  const storeGroupSections = useMemo(() => {
    if (!modalState) {
      return [];
    }

    return getActiveBundleStoreGroupsForVariant(activeBundleStoreGroups, modalState.variantId)
      .map((detail) => ({
        storeGroupId: detail.storeGroupId,
        storeGroupName:
          modalState.storeGroupNamesById[String(detail.storeGroupId).toLowerCase()] ?? detail.storeGroupId,
        bundleLabels: detail.bundleLabels ?? [],
      }))
      .filter((section) => section.bundleLabels.length > 0);
  }, [activeBundleStoreGroups, modalState]);

  const productLabel = modalState
    ? `${modalState.productName}${modalState.productDescription ? ` ${modalState.productDescription}` : ''}`
    : '';

  return (
    <Modal
      className={styles.modal}
      maxWidth="670px"
      headerContent={
        <div className={styles.header}>{t('deactivateProducts.activeBundlesModal.title', { productLabel })}</div>
      }
      showTitleDivider
      onClose={onClose}
      isOpen={isOpen}
      bodyClass={styles.modalBody}
      bodyContent={
        modalState ? (
          <div className={styles.body}>
            <p className={styles.description}>{t('deactivateProducts.activeBundlesModal.description')}</p>
            {isLoading && (
              <p className={styles.loadingMessage} data-testid="active-bundles-loading">
                {t('common.loading')}
              </p>
            )}
            {isError && (
              <p className={styles.errorMessage} data-testid="active-bundles-error">
                {t('deactivateProducts.activeBundlesModal.error')}
              </p>
            )}
            {!isLoading && !isError && storeGroupSections.length === 0 && (
              <p className={styles.emptyMessage} data-testid="active-bundles-empty">
                {t('deactivateProducts.activeBundlesModal.empty')}
              </p>
            )}
            {!isLoading && !isError && storeGroupSections.length > 0 && (
              <div className={styles.sections}>
                {storeGroupSections.map((section) => (
                  <div className={styles.storeGroupSection} key={section.storeGroupId}>
                    <p className={styles.storeGroupName}>
                      {t('deactivateProducts.activeBundlesModal.storeGroupLabel', {
                        storeGroupName: section.storeGroupName,
                      })}
                    </p>
                    <div className={styles.listContainer}>
                      <div className={styles.listBody}>
                        <div className={styles.listHeader}>
                          <span>{t('deactivateProducts.activeBundlesModal.table.bundle')}</span>
                        </div>
                        {section.bundleLabels.map((bundleLabel, index) => (
                          <div
                            key={bundleLabel}
                            className={classNames(styles.listRow, { [styles.listRowEven]: index % 2 === 0 })}
                            data-testid="active-bundle-row"
                          >
                            {bundleLabel}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null
      }
      footerContent={
        <div className={styles.footer}>
          <Button onClick={onClose} variant={ButtonVariants.Primary} data-testid="active-bundles-ok">
            {t('deactivateProducts.activeBundlesModal.ok')}
          </Button>
        </div>
      }
    />
  );
};

export default ProductActiveBundlesModal;
